import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { StatusBadge } from "@/components/StatusBadge";
import { PdfUpload } from "@/components/PdfUpload";
import { FileLink } from "@/components/FileLink";
import { toast } from "sonner";
import { Briefcase, Clock, FileText, Calendar, Download, Plus, Loader2, DollarSign } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  { id: "jobs", label: "My Jobs", icon: Briefcase },
  { id: "timesheets", label: "Timesheets", icon: Clock },
  { id: "invoices", label: "Invoices", icon: FileText },
];

export default function ContractorDashboard() {
  const [tab, setTab] = useState("jobs");
  const [jobs, setJobs] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const load = async () => {
    try {
      const [j, t, i] = await Promise.all([
        api.get("/contractor/jobs"),
        api.get("/contractor/timesheets"),
        api.get("/contractor/invoices"),
      ]);
      setJobs(j.data.jobs);
      setTimesheets(t.data.timesheets);
      setInvoices(i.data.invoices);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (user?.must_change_password) return <ForcePasswordChange />;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar title="Creative Portal" />
      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        <h1 className="font-bebas text-5xl mb-8">YOUR WORKSPACE</h1>

        <div className="flex border-b border-[#2A2A2A] mb-8">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-5 py-3 text-sm uppercase tracking-[0.1em] border-b-2 -mb-px transition-colors ${tab === t.id ? "border-[#FF4D00] text-white" : "border-transparent text-[#A1A1AA] hover:text-white"}`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#FF4D00]" /></div>
        ) : (
          <>
            {tab === "jobs" && <JobsTab jobs={jobs} />}
            {tab === "timesheets" && <TimesheetsTab jobs={jobs} timesheets={timesheets} reload={load} />}
            {tab === "invoices" && <InvoicesTab jobs={jobs} invoices={invoices} reload={load} />}
          </>
        )}
      </main>
    </div>
  );
}

function Empty({ text }) {
  return <div className="border border-dashed border-[#2A2A2A] p-12 text-center text-sm text-[#A1A1AA]">{text}</div>;
}

function JobsTab({ jobs }) {
  if (!jobs.length) return <Empty text="No jobs assigned to you yet. Check back soon." />;
  return (
    <div className="grid md:grid-cols-2 gap-px bg-[#2A2A2A] border border-[#2A2A2A]" data-testid="contractor-jobs-list">
      {jobs.map((j) => (
        <div key={j.id} className="bg-[#111111] p-7">
          <div className="flex items-start justify-between mb-3">
            <div>
              {j.job_number && <div className="text-[10px] uppercase tracking-[0.2em] text-[#FF4D00] mb-1">Job #{j.job_number}</div>}
              <h3 className="font-bebas text-2xl tracking-wide">{j.title}</h3>
            </div>
            <StatusBadge status={j.status} />
          </div>
          <p className="text-sm text-[#A1A1AA] leading-relaxed mb-5">{j.scope}</p>
          <div className="flex items-center gap-2 text-xs text-[#A1A1AA] mb-4"><Calendar className="w-4 h-4 text-[#FF4D00]" /> Due {j.due_date}</div>
          {j.assets?.length > 0 && (
            <div className="border-t border-[#2A2A2A] pt-4 space-y-2">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-2">Assets</div>
              {j.assets.map((a) => (
                <FileLink key={a.id} fileId={a.id} testId={`asset-${a.id}`} className="flex items-center gap-2 text-sm text-white hover:text-[#FF4D00] transition-colors">
                  <Download className="w-4 h-4" /> {a.original_filename}
                </FileLink>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function TimesheetsTab({ jobs, timesheets, reload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ job_id: "", start_time: "", end_time: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const inputCls = "w-full bg-[#050505] border border-[#2A2A2A] px-4 py-3 text-white text-sm focus:outline-none focus:border-[#FF4D00] transition-colors";

  const submit = async () => {
    if (!form.job_id || !form.start_time || !form.end_time) { toast.error("Fill project, start and end time"); return; }
    setSaving(true);
    try {
      await api.post("/contractor/timesheets", form);
      toast.success("Timesheet submitted");
      setOpen(false);
      setForm({ job_id: "", start_time: "", end_time: "", notes: "" });
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <div className="text-sm text-[#A1A1AA]">{timesheets.length} entries</div>
        <button data-testid="new-timesheet-btn" onClick={() => setOpen(!open)} className="inline-flex items-center gap-2 bg-[#FF4D00] text-white px-4 py-2 text-sm hover:bg-[#E64500] transition-colors"><Plus className="w-4 h-4" /> Log time</button>
      </div>

      {open && (
        <div className="border border-[#2A2A2A] bg-[#111111] p-6 mb-6 space-y-4" data-testid="timesheet-form">
          <select data-testid="timesheet-job-select" className={inputCls} value={form.job_id} onChange={(e) => setForm({ ...form, job_id: e.target.value })}>
            <option value="">Select project</option>
            {jobs.map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}
          </select>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] block mb-1">Start</label>
              <input data-testid="timesheet-start-input" type="datetime-local" className={inputCls} value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] block mb-1">End</label>
              <input data-testid="timesheet-end-input" type="datetime-local" className={inputCls} value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} />
            </div>
          </div>
          <textarea data-testid="timesheet-notes-input" className={inputCls} placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          <button data-testid="timesheet-submit-btn" onClick={submit} disabled={saving} className="bg-[#FF4D00] text-white px-6 py-3 text-sm hover:bg-[#E64500] transition-colors flex items-center gap-2 disabled:opacity-60">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Submit timesheet</button>
        </div>
      )}

      {!timesheets.length ? <Empty text="No timesheets submitted yet." /> : (
        <div className="border border-[#2A2A2A]" data-testid="timesheets-list">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-[#2A2A2A] text-left text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">
              <th className="p-4">Project</th><th className="p-4">Start</th><th className="p-4">End</th><th className="p-4">Hours</th><th className="p-4">Status</th>
            </tr></thead>
            <tbody>
              {timesheets.map((t) => (
                <tr key={t.id} className="border-b border-[#2A2A2A] hover:bg-[#111111] transition-colors">
                  <td className="p-4 text-white">{t.job_title}</td>
                  <td className="p-4 text-[#A1A1AA]">{t.start_time.replace("T", " ")}</td>
                  <td className="p-4 text-[#A1A1AA]">{t.end_time.replace("T", " ")}</td>
                  <td className="p-4 text-white">{t.hours}h</td>
                  <td className="p-4"><StatusBadge status={t.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function InvoicesTab({ jobs, invoices, reload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ job_id: "", amount: "" });
  const [pdf, setPdf] = useState(null);
  const [saving, setSaving] = useState(false);
  const inputCls = "w-full bg-[#050505] border border-[#2A2A2A] px-4 py-3 text-white text-sm focus:outline-none focus:border-[#FF4D00] transition-colors";

  const submit = async () => {
    if (!form.amount || !pdf) { toast.error("Enter amount and upload invoice PDF"); return; }
    setSaving(true);
    try {
      await api.post("/contractor/invoices", { job_id: form.job_id || null, amount: parseFloat(form.amount), invoice_file_id: pdf.id });
      toast.success("Invoice uploaded");
      setOpen(false); setForm({ job_id: "", amount: "" }); setPdf(null); reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <div className="text-sm text-[#A1A1AA]">{invoices.length} invoices</div>
        <button data-testid="new-invoice-btn" onClick={() => setOpen(!open)} className="inline-flex items-center gap-2 bg-[#FF4D00] text-white px-4 py-2 text-sm hover:bg-[#E64500] transition-colors"><Plus className="w-4 h-4" /> Upload invoice</button>
      </div>

      {open && (
        <div className="border border-[#2A2A2A] bg-[#111111] p-6 mb-6 space-y-4" data-testid="invoice-form">
          <select data-testid="invoice-job-select" className={inputCls} value={form.job_id} onChange={(e) => setForm({ ...form, job_id: e.target.value })}>
            <option value="">General (no specific project)</option>
            {jobs.map((j) => <option key={j.id} value={j.id}>{`${j.job_number ? j.job_number + " | " : ""}${j.title}`}</option>)}
          </select>
          <div className="relative">
            <DollarSign className="w-4 h-4 text-[#A1A1AA] absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="invoice-amount-input" type="number" step="0.01" className={`${inputCls} pl-9`} placeholder="Amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          </div>
          <PdfUpload testId="invoice-upload" category="invoice" value={pdf} onUploaded={setPdf} label="Invoice PDF" />
          <button data-testid="invoice-submit-btn" onClick={submit} disabled={saving} className="bg-[#FF4D00] text-white px-6 py-3 text-sm hover:bg-[#E64500] transition-colors flex items-center gap-2 disabled:opacity-60">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Submit invoice</button>
        </div>
      )}

      {!invoices.length ? <Empty text="No invoices uploaded yet." /> : (
        <div className="border border-[#2A2A2A]" data-testid="invoices-list">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-[#2A2A2A] text-left text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">
              <th className="p-4">Project</th><th className="p-4">Amount</th><th className="p-4">Submitted</th><th className="p-4">PDF</th><th className="p-4">Status</th>
            </tr></thead>
            <tbody>
              {invoices.map((v) => (
                <tr key={v.id} className="border-b border-[#2A2A2A] hover:bg-[#111111] transition-colors">
                  <td className="p-4 text-white">{v.job_title || "General"}</td>
                  <td className="p-4 text-white">${v.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  <td className="p-4 text-[#A1A1AA]">{new Date(v.submitted_at).toLocaleDateString()}</td>
                  <td className="p-4"><FileLink fileId={v.invoice_file_id} testId={`invoice-view-${v.id}`} className="text-[#FF4D00] hover:underline inline-flex items-center gap-1"><Download className="w-4 h-4" /> View</FileLink></td>
                  <td className="p-4"><StatusBadge status={v.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ForcePasswordChange() {
  const { changePassword } = useAuth();
  const [cur, setCur] = useState("");
  const [np, setNp] = useState("");
  const [np2, setNp2] = useState("");
  const [saving, setSaving] = useState(false);
  const cls = "w-full bg-[#050505] border border-[#2A2A2A] px-4 py-3 text-white text-sm focus:outline-none focus:border-[#FF4D00] transition-colors";
  const submit = async () => {
    if (!cur) return toast.error("Enter your temporary password");
    if (np.length < 8) return toast.error("New password must be at least 8 characters");
    if (np !== np2) return toast.error("Passwords do not match");
    setSaving(true);
    try { await changePassword(cur, np); toast.success("Password updated — welcome to the portal!"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-[#0A0A0A] border border-[#2A2A2A] shadow-[0_0_60px_rgba(255,77,0,0.15)]" data-testid="force-password-modal">
        <div className="h-1 bg-[#FF4D00]" />
        <div className="p-8">
          <div className="text-[11px] uppercase tracking-[0.4em] text-[#FF4D00] mb-3">Account Security</div>
          <h1 className="font-bebas text-4xl mb-2">SET YOUR PASSWORD</h1>
          <p className="text-sm text-[#A1A1AA] mb-6">Welcome to the Splash creative portal. For your security, please replace the temporary password we emailed you before continuing.</p>
          <div className="space-y-3">
            <input data-testid="fpc-current" type="password" className={cls} placeholder="Temporary password" value={cur} onChange={(e) => setCur(e.target.value)} />
            <input data-testid="fpc-new" type="password" className={cls} placeholder="New password (min 8 characters)" value={np} onChange={(e) => setNp(e.target.value)} />
            <input data-testid="fpc-confirm" type="password" className={cls} placeholder="Confirm new password" value={np2} onChange={(e) => setNp2(e.target.value)} />
          </div>
          <button data-testid="fpc-submit" onClick={submit} disabled={saving} className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#FF4D00] text-white px-6 py-3 text-sm uppercase tracking-[0.15em] hover:bg-[#E64500] transition-colors disabled:opacity-60">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Set password & continue
          </button>
        </div>
      </div>
    </div>
  );
}

